import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getFounderById, updateFounder } from '../../api/contactus'
import { FiLoader } from 'react-icons/fi'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

const EditFounder = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      amount: '',
      companyName: '',
      email: '',
      fullName: '',
      mobile: '',
    },
  })

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['founderById', id],
    queryFn: () => getFounderById(id),
    enabled: !!id,
  })

  // Populate form on load
  useEffect(() => {
    if (data) {
      setValue('amount', data.amount || '')
      setValue('companyName', data.companyName || '')
      setValue('email', data.email || '')
      setValue('fullName', data.fullName || '')
      setValue('mobile', data.mobile || '')
    }
  }, [data, setValue])

  const Mutation = useMutation({
    mutationFn: (data) => updateFounder(data, id),
    onSuccess: () => {
      toast.success('founder Updated')
      navigate(-1)
    },
  })

  const onSubmit = async (formData) => {
    console.log('Updated Data:', formData)

    Mutation.mutate(formData)

    // toast.success('Founder updated successfully!')
  }

  // Loading
  if (isPending) {
    return (
      <div className="flex justify-center items-center h-80">
        <FiLoader className="w-8 h-8 animate-spin text-orange-500" />
        <span className="ml-3 text-gray-600 text-lg font-medium">
          Loading founder data...
        </span>
      </div>
    )
  }

  // Error
  if (isError) {
    return (
      <div className="text-center text-red-600 mt-20">
        <p className="text-lg font-semibold">Failed to load data 😞</p>
        <p className="text-gray-500">{error?.message}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {/* Full Name */}
        <div className="bg-white p-6 rounded-xl shadow">
          <label className="font-semibold text-gray-800 mb-2 block">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('fullName', { required: 'Name is required' })}
            className={`border rounded-lg p-3 w-full text-base ${
              errors.fullName
                ? 'border-red-500'
                : 'border-gray-300 focus:border-orange-500'
            }`}
          />
          {errors.fullName && (
            <p className="text-red-500 text-sm mt-1">
              {errors.fullName.message}
            </p>
          )}
        </div>

        {/* Mobile */}
        <div className="bg-white p-6 rounded-xl shadow">
          <label className="font-semibold text-gray-800 mb-2 block">
            Mobile <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            maxLength="10"
            {...register('mobile', { required: 'Mobile is required' })}
            className={`border rounded-lg p-3 w-full text-base ${
              errors.mobile
                ? 'border-red-500'
                : 'border-gray-300 focus:border-orange-500'
            }`}
          />
          {errors.mobile && (
            <p className="text-red-500 text-sm mt-1">{errors.mobile.message}</p>
          )}
        </div>

        {/* Email */}
        <div className="bg-white p-6 rounded-xl shadow">
          <label className="font-semibold text-gray-800 mb-2 block">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            {...register('email', { required: 'Email is required' })}
            className={`border rounded-lg p-3 w-full text-base ${
              errors.email
                ? 'border-red-500'
                : 'border-gray-300 focus:border-orange-500'
            }`}
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>

        {/* Company Name */}
        <div className="bg-white p-6 rounded-xl shadow">
          <label className="font-semibold text-gray-800 mb-2 block">
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('companyName', {
              required: 'Company name is required',
            })}
            className={`border rounded-lg p-3 w-full text-base ${
              errors.companyName
                ? 'border-red-500'
                : 'border-gray-300 focus:border-orange-500'
            }`}
          />
          {errors.companyName && (
            <p className="text-red-500 text-sm mt-1">
              {errors.companyName.message}
            </p>
          )}
        </div>

        {/* Amount */}
        <div className="bg-white p-6 rounded-xl shadow">
          <label className="font-semibold text-gray-800 mb-2 block">
            Amount <span className="text-red-500">*</span>
          </label>
          <select
            {...register('amount', { required: 'Please select amount type' })}
            className="border rounded-lg p-3 w-full text-base border-gray-300 focus:border-orange-500"
          >
            <option value="">Select One</option>
            <option value="PAID">Paid</option>
            <option value="UNPAID">Unpaid</option>
          </select>

          {errors.amount && (
            <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>
          )}
        </div>

        {/* Submit Button */}

        <div className="col-span-full flex justify-between">
          <button
            onClick={() => navigate(-1)}
            className="bg-gray-600 text-white px-6 py-3 rounded-lg shadow hover:bg-gray-700 transition"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-orange-600 text-white px-6 py-3 rounded-lg shadow hover:bg-orange-700 transition disabled:opacity-50"
          >
            {isSubmitting ? 'Updating...' : 'Update Founder'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default EditFounder
